import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import useContractDraftingService from '../../app/services/hooks/useContractDraftingService';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';
import { AppContext } from '../../contexts/AppContext';

interface ContractType {
    id: number;
    name: string;
    description: string;
    icon: string;
    price: string;
    has_articles: boolean;
    is_signature_only: boolean;
}

const SelectContractType: React.FC = () => {
    const history = useHistory();
    const { showLoading, hideLoading } = useContext(AppContext);
    const { getContractTypes, createContractRequest, fetchingContractTypes, fetchingCreateRequest } = useContractDraftingService();
    const [contractTypes, setContractTypes] = useState<ContractType[]>([]);
    const [selectedType, setSelectedType] = useState<number | null>(null);

    useEffect(() => {
        loadContractTypes();
    }, []);

    useEffect(() => {
        if (fetchingContractTypes || fetchingCreateRequest) {
            showLoading?.();
        } else {
            hideLoading?.();
        }
    }, [fetchingContractTypes, fetchingCreateRequest]);

    const loadContractTypes = () => {
        getContractTypes({
            onSuccess: (res) => {
                setContractTypes(res.data.contract_types || []);
            },
            onError: (res) => {
                toast.error(res.message || 'Error al cargar tipos de contrato');
            }
        });
    };

    const handleSelectType = (typeId: number, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        if (fetchingCreateRequest) {
            return;
        }
        
        setSelectedType(typeId);
        
        createContractRequest(typeId, {
            onSuccess: (res) => {
                const requestId = res.data?.contract_request?.id || res.data?.id;
                if (requestId) {
                    if (res.data?.is_signature_only) {
                        // El documento ya viene listo: se salta formulario, preview y pago.
                        toast.success('Documento generado. Ya puedes agregar los firmantes.');
                        history.push(`/intranet/contracts/${requestId}/payment-success`);
                    } else {
                        toast.success('Solicitud creada exitosamente');
                        history.push(`/intranet/contracts/${requestId}/form`);
                    }
                } else {
                    toast.error('Error: No se recibió el ID de la solicitud');
                    console.error('Respuesta del servidor:', res);
                    setSelectedType(null);
                }
            },
            onError: (res) => {
                toast.error(res.message || 'Error al crear la solicitud');
                console.error('Error al crear solicitud:', res);
                setSelectedType(null);
            }
        });
    };

    // Mapear iconos según el nombre del contrato
    const getContractIcon = (name: string): string => {
        const nameLower = name.toLowerCase();
        if (nameLower.includes('honorario') || nameLower.includes('servicio')) return 'fa-handshake';
        if (nameLower.includes('trabajo') || nameLower.includes('laboral')) return 'fa-briefcase';
        if (nameLower.includes('arriendo') || nameLower.includes('alquiler')) return 'fa-home';
        if (nameLower.includes('compraventa') || nameLower.includes('venta')) return 'fa-shopping-cart';
        if (nameLower.includes('confidencial') || nameLower.includes('nda')) return 'fa-user-secret';
        if (nameLower.includes('sociedad') || nameLower.includes('empresa')) return 'fa-building';
        if (nameLower.includes('poder') || nameLower.includes('mandato')) return 'fa-gavel';
        if (nameLower.includes('prestamo') || nameLower.includes('mutuo')) return 'fa-hand-holding-usd';
        return 'fa-file-contract';
    };

    // Colores para las tarjetas
    const getCardColors = (index: number): { bg: string; gradient: string } => {
        const colors = [
            { bg: '#001849', gradient: 'linear-gradient(135deg, #001849 0%, #1a3a6e 100%)' },
            { bg: '#0d47a1', gradient: 'linear-gradient(135deg, #0d47a1 0%, #1976d2 100%)' },
            { bg: '#00695c', gradient: 'linear-gradient(135deg, #00695c 0%, #00897b 100%)' },
            { bg: '#4527a0', gradient: 'linear-gradient(135deg, #4527a0 0%, #7e57c2 100%)' },
            { bg: '#c62828', gradient: 'linear-gradient(135deg, #c62828 0%, #ef5350 100%)' },
            { bg: '#2e7d32', gradient: 'linear-gradient(135deg, #2e7d32 0%, #66bb6a 100%)' },
        ];
        return colors[index % colors.length];
    };

    return (
        <div className="container-fluid py-4">
            <div className="row justify-content-center">
                <div className="col-lg-10 col-xl-9">
                    {/* Header */}
                    <div className="mb-4">
                        <div className="d-flex align-items-center mb-2">
                            <div 
                                className="d-flex align-items-center justify-content-center me-3"
                                style={{
                                    width: '50px',
                                    height: '50px',
                                    borderRadius: '12px',
                                    background: 'linear-gradient(135deg, #001849 0%, #1a3a6e 100%)',
                                    color: 'white',
                                    fontSize: '22px',
                                    boxShadow: '0 4px 15px rgba(0, 24, 73, 0.3)'
                                }}
                            >
                                <i className="fas fa-file-signature"></i>
                            </div>
                            <div>
                                <h4 className="mb-0" style={{ color: '#001849', fontWeight: 600 }}>Redacción de Contratos</h4>
                                <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                    Selecciona el tipo de contrato que necesitas
                                </p>
                            </div>
                        </div>
                    </div>

                    {contractTypes.length === 0 ? (
                        <div className="card border-0 shadow-sm">
                            <div className="card-body text-center py-5">
                                <div 
                                    className="d-inline-flex align-items-center justify-content-center mb-3"
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '50%',
                                        backgroundColor: '#e3f2fd',
                                        color: '#1976d2',
                                        fontSize: '24px'
                                    }}
                                >
                                    <i className="fas fa-info-circle"></i>
                                </div>
                                <h5>No hay contratos disponibles</h5>
                                <p className="text-muted mb-0">
                                    No hay tipos de contrato disponibles en este momento.
                                </p>
                            </div>
                        </div>
                    ) : (
                        <>
                            <div className="row g-4">
                                {contractTypes.map((type, index) => {
                                    const colors = getCardColors(index);
                                    const isSelected = selectedType === type.id;
                                    const isDisabled = fetchingCreateRequest && !isSelected;
                                    
                                    return (
                                        <div key={type.id} className="col-md-6">
                                            <div
                                                className="card border-0 h-100"
                                                style={{
                                                    cursor: fetchingCreateRequest ? (isSelected ? 'wait' : 'not-allowed') : 'pointer',
                                                    transition: 'all 0.3s ease',
                                                    boxShadow: isSelected 
                                                        ? '0 8px 25px rgba(0, 24, 73, 0.4)' 
                                                        : '0 4px 15px rgba(0, 0, 0, 0.08)',
                                                    opacity: isDisabled ? 0.5 : 1,
                                                    transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                                                    borderRadius: '12px',
                                                    overflow: 'hidden'
                                                }}
                                                onClick={(e) => !isDisabled && handleSelectType(type.id, e)}
                                                onMouseEnter={(e) => {
                                                    if (!fetchingCreateRequest) {
                                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                                        e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 24, 73, 0.2)';
                                                    }
                                                }}
                                                onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                        e.currentTarget.style.transform = 'scale(1)';
                                                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.08)';
                                                    }
                                                }}
                                            >
                                                {/* Header de la tarjeta con color */}
                                                <div 
                                                    style={{
                                                        background: colors.gradient,
                                                        padding: '20px 24px',
                                                        color: 'white'
                                                    }}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <div 
                                                            className="d-flex align-items-center justify-content-center me-3"
                                                            style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '12px',
                                                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                                fontSize: '22px'
                                                            }}
                                                        >
                                                            <i className={`fas ${getContractIcon(type.name)}`}></i>
                                                        </div>
                                                        <div className="flex-grow-1">
                                                            <h5 className="mb-1 text-white" style={{ fontWeight: 600 }}>{type.name}</h5>
                                                            <div 
                                                                className="d-inline-block px-2 py-1 rounded text-white"
                                                                style={{
                                                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                                                    fontSize: '13px',
                                                                    fontWeight: 500
                                                                }}
                                                            >
                                                                {type.is_signature_only
                                                                    ? 'Sin costo'
                                                                    : `$${parseInt(type.price).toLocaleString('es-CL')} CLP`
                                                                }
                                                            </div>
                                                        </div>
                                                        {isSelected && (
                                                            <div className="spinner-border spinner-border-sm text-light" role="status">
                                                                <span className="visually-hidden">Cargando...</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Contenido de la tarjeta */}
                                                <div className="card-body" style={{ padding: '20px 24px' }}>
                                                    <p className="text-muted mb-3" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                                        {type.description || 'Contrato profesional adaptado a la legislación chilena vigente.'}
                                                    </p>
                                                    
                                                    <div className="d-flex flex-wrap gap-2">
                                                        {type.is_signature_only && (
                                                            <span 
                                                                className="badge"
                                                                style={{
                                                                    backgroundColor: '#ede7f6',
                                                                    color: '#4527a0',
                                                                    fontWeight: 500,
                                                                    padding: '6px 10px'
                                                                }}
                                                            >
                                                                <i className="fas fa-file-signature me-1"></i>
                                                                Solo firmas
                                                            </span>
                                                        )}
                                                        <span 
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: '#e8f5e9',
                                                                color: '#2e7d32',
                                                                fontWeight: 500,
                                                                padding: '6px 10px'
                                                            }}
                                                        >
                                                            <i className="fas fa-check me-1"></i>
                                                            Ley Chilena
                                                        </span>
                                                        <span 
                                                            className="badge"
                                                            style={{
                                                                backgroundColor: '#e3f2fd',
                                                                color: '#1565c0',
                                                                fontWeight: 500,
                                                                padding: '6px 10px'
                                                            }}
                                                        >
                                                            <i className="fas fa-edit me-1"></i>
                                                            Editable
                                                        </span>
                                                        {type.has_articles && (
                                                            <span 
                                                                className="badge"
                                                                style={{
                                                                    backgroundColor: '#fff3e0',
                                                                    color: '#e65100',
                                                                    fontWeight: 500,
                                                                    padding: '6px 10px'
                                                                }}
                                                            >
                                                                <i className="fas fa-list-ul me-1"></i>
                                                                Artículos opcionales
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Footer con CTA */}
                                                <div 
                                                    className="card-footer border-0 bg-transparent"
                                                    style={{ padding: '0 24px 20px' }}
                                                >
                                                    <button
                                                        type="button"
                                                        className="btn w-100"
                                                        disabled={isDisabled}
                                                        style={{
                                                            background: colors.gradient,
                                                            color: 'white',
                                                            fontWeight: 500,
                                                            padding: '12px',
                                                            borderRadius: '8px',
                                                            border: 'none'
                                                        }}
                                                    >
                                                        {isSelected ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                                Creando...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-arrow-right me-2"></i>
                                                                {type.is_signature_only
                                                                    ? 'Generar y agregar firmantes'
                                                                    : 'Crear este contrato'
                                                                }
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="text-center mt-5">
                                <ButtonGeneral
                                    type="button"
                                    variant="outline"
                                    title="Volver a mis contratos"
                                    onClick={() => history.push('/intranet/contracts')}
                                    icon="fa-arrow-left"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SelectContractType;
